/**
 * File Operation Handlers
 * Extracted from projectDataUtils.ts applyOperation
 * Handles file attachment operations
 */

import type { ProjectData, ProjectNode, NodeOperation } from '@/shared/types/projectData';
import { cloneProjectData } from '@/shared/utils/projectDataUtils';

const findNodeById = (data: ProjectData, nodeId: string) =>
  data.nodes.find(n => n.id === nodeId) || null;

function cloneProjectDataWithFiles(data: ProjectData): ProjectData {
  const updated = cloneProjectData(data);
  updated.files = Array.isArray(updated.files)
    ? updated.files.map((file) => ({ ...file }))
    : [];
  return updated;
}

function refreshAttachmentFlags(data: ProjectData, node: ProjectNode): void {
  const attachmentIds = node.attachments || [];
  const filesById = new Map((data.files || []).map(file => [file.id, file]));
  const isImage = (id: string): boolean => filesById.get(id)?.mimeType?.toLowerCase().startsWith('image/') === true;
  node.attachmentCount = attachmentIds.length;
  node.hasImages = attachmentIds.some(isImage);
  node.hasDocuments = attachmentIds.some(id => !isImage(id));
}

export function handleAttachFile(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.nodeId || !op.fileId) return data;
  const node = findNodeById(data, op.nodeId);
  if (!node) return data;

  const updated = cloneProjectDataWithFiles(data);
  const updatedNode = updated.nodes.find(n => n.id === op.nodeId);
  if (updatedNode) {
    // Add file to attachments array
    if (!updatedNode.attachments) {
      updatedNode.attachments = [];
    }
    if (!updatedNode.attachments.includes(op.fileId)) {
      updatedNode.attachments = [...updatedNode.attachments, op.fileId];
    }

    // Add file data to project if provided
    if (op.fileData && updated.files) {
      const fileExists = updated.files.find(f => f.id === op.fileId);
      if (!fileExists) {
        updated.files = [...updated.files, { ...op.fileData }];
      }
    }
    refreshAttachmentFlags(updated, updatedNode);
  }
  return updated;
}

export function handleDetachFile(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.nodeId || !op.fileId) return data;
  const node = findNodeById(data, op.nodeId);
  if (!node) return data;

  const updated = cloneProjectDataWithFiles(data);
  const updatedNode = updated.nodes.find(n => n.id === op.nodeId);
  if (updatedNode && updatedNode.attachments) {
    // Remove file from attachments array
    updatedNode.attachments = updatedNode.attachments.filter(id => id !== op.fileId);
    refreshAttachmentFlags(updated, updatedNode);
  }
  return updated;
}

export function handleDeleteFile(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.fileId) return data;
  const updated = cloneProjectDataWithFiles(data);

  // Remove file from files array
  if (updated.files) {
    updated.files = updated.files.filter(f => f.id !== op.fileId);
  }

  // Remove file reference from all nodes
  updated.nodes.forEach(node => {
    if (node.attachments && op.fileId && node.attachments.includes(op.fileId)) {
      node.attachments = node.attachments.filter(id => id !== op.fileId);
      refreshAttachmentFlags(updated, node);
    }
  });

  return updated;
}

export function handleRenameFile(data: ProjectData, op: NodeOperation): ProjectData {
  if (!op.fileId || !op.newFileName) return data;
  const updated = cloneProjectDataWithFiles(data);
  const nextFileName = op.newFileName;

  // Update file name in files array
  if (updated.files) {
    updated.files = updated.files.map((file) => (
      file.id === op.fileId
        ? { ...file, originalName: nextFileName, filename: nextFileName }
        : file
    ));
  }

  return updated;
}
